import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Container,
  Divider, FormControlLabel, InputAdornment, List, ListItemButton, ListItemText,
  Paper, Stack, Switch, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import {
  AccountTree, AssignmentTurnedIn, Business, DataObject, Download, PlayArrow, Refresh, Save,
  Search, Shield, Terminal,
} from "@mui/icons-material";
import { AdminCommandResult, SuperAdminBusiness } from "../../models/superAdmin.model";
import { superAdminApi } from "../../services/superAdmin.service";
import { DashboardHeading } from "../dashboard/DashboardPage";
import ConversationBillingDashboard from "../ConversationBilling/ConversationBillingDashboard";

const messageFor = (error: any) =>
  error?.response?.data?.detail || error?.message || "Unable to load super-admin data.";

function downloadJson(filename: string, value: Record<string, any>) {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function JsonEditor({
  title, value, filename, editMode, onSave,
}: {
  title: string;
  value: Record<string, any>;
  filename: string;
  editMode: boolean;
  onSave: (next: Record<string, any>) => Promise<void>;
}) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [parseError, setParseError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(JSON.stringify(value, null, 2));
    setParseError("");
  }, [editMode, value]);

  const parse = () => {
    try {
      const parsed = JSON.parse(text);
      if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
        throw new Error("The root JSON value must be an object.");
      }
      setParseError("");
      return parsed as Record<string, any>;
    } catch (error: any) {
      setParseError(error.message || "Invalid JSON");
      return null;
    }
  };

  const format = () => {
    const parsed = parse();
    if (parsed) setText(JSON.stringify(parsed, null, 2));
  };

  const save = async () => {
    const parsed = parse();
    if (!parsed) return;
    setSaving(true);
    try { await onSave(parsed); }
    finally { setSaving(false); }
  };

  return <Stack spacing={2}>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
      <Box>
        <Typography variant="h6" fontWeight={700}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          Valid JSON is required. Redacted secrets and MongoDB identity/reference fields are preserved by the server.
        </Typography>
      </Box>
      <Stack direction="row" gap={1}>
        {editMode && <Button startIcon={<DataObject />} onClick={format}>Format JSON</Button>}
        <Button startIcon={<Download />} onClick={() => downloadJson(filename, value)}>Download</Button>
        {editMode && <Button variant="contained" startIcon={<Save />} disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save JSON"}
        </Button>}
      </Stack>
    </Stack>
    {parseError && <Alert severity="error">{parseError}</Alert>}
    {editMode ? <TextField
      multiline fullWidth minRows={20} maxRows={30} value={text}
      onChange={(event) => setText(event.target.value)}
      error={Boolean(parseError)}
      inputProps={{ spellCheck: false, "aria-label": `${title} JSON editor` }}
      sx={{
        "& textarea": {
          fontFamily: "Consolas, Monaco, 'Courier New', monospace",
          fontSize: 13,
          lineHeight: 1.55,
        },
      }}
    /> : <Box component="pre" sx={{
      m: 0, p: 2, bgcolor: "grey.900", color: "grey.100", borderRadius: 2,
      overflow: "auto", maxHeight: 600, textAlign: "left",
      fontFamily: "Consolas, Monaco, 'Courier New', monospace", fontSize: 12,
      lineHeight: 1.55,
    }}>{JSON.stringify(value, null, 2)}</Box>}
  </Stack>;
}

function SettingsOverview({
  settings, editMode, onSaved,
}: {
  settings: Record<string, any>;
  editMode: boolean;
  onSaved: (update: Record<string, any>) => void;
}) {
  const business = settings.businessSettings || {};
  const connectors = settings.connectorSettings || [];
  const [dailyCap, setDailyCap] = useState(String(settings.DAILY_MAX_CAP ?? 149));
  const [rate, setRate] = useState(String(settings.PER_CONVERSATION_RATE ?? "2.00"));
  const [currency, setCurrency] = useState(String(settings.BILLING_CURRENCY ?? "INR"));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setDailyCap(String(settings.DAILY_MAX_CAP ?? 149));
    setRate(String(settings.PER_CONVERSATION_RATE ?? "2.00"));
    setCurrency(String(settings.BILLING_CURRENCY ?? "INR"));
  }, [editMode, settings]);

  const saveCommercialSettings = async () => {
    const parsedCap = Number(dailyCap);
    const parsedRate = Number(rate);
    if (!Number.isInteger(parsedCap) || parsedCap < 0) {
      setError("Daily maximum cap must be a non-negative whole number."); return;
    }
    if (!Number.isFinite(parsedRate) || parsedRate <= 0) {
      setError("Per-conversation rate must be greater than zero."); return;
    }
    if (!/^[A-Za-z]{3}$/.test(currency.trim())) {
      setError("Currency must be a three-letter code such as INR."); return;
    }
    setSaving(true); setError(""); setMessage("");
    try {
      const update = await superAdminApi.updateCommercialSettings(settings.settingsId, {
        DAILY_MAX_CAP: parsedCap,
        PER_CONVERSATION_RATE: parsedRate.toFixed(2),
        BILLING_CURRENCY: currency.trim().toUpperCase(),
      });
      onSaved(update);
      setMessage("Daily cap and billing settings saved.");
    } catch (e) { setError(messageFor(e)); }
    finally { setSaving(false); }
  };

  return <Stack spacing={2}>
    {message && <Alert severity="success">{message}</Alert>}
    {error && <Alert severity="error">{error}</Alert>}
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
      <Card variant="outlined"><CardContent><Typography color="text.secondary" variant="overline">Business</Typography><Typography variant="h6">{business.name || "Unnamed"}</Typography><Typography>{business.fullPhoneNumber || "No phone"}</Typography><Typography variant="body2">Business ID: {business.businessId || "Not connected"}</Typography><Typography variant="body2">Timezone: {business.timezone || "Asia/Kolkata"}</Typography></CardContent></Card>
      <Card variant="outlined"><CardContent><Typography color="text.secondary" variant="overline">Connectors</Typography><Typography variant="h6">{connectors.length} configured</Typography><Typography variant="body2">Sensitive credentials are always redacted and cannot be overwritten from this console.</Typography></CardContent></Card>
    </Box>
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight={700} mb={2}>Limits and billing</Typography>
      {editMode ? <>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
        <TextField label="DAILY_MAX_CAP" type="number" value={dailyCap} onChange={(e) => setDailyCap(e.target.value)} inputProps={{ min: 0, step: 1 }} />
        <TextField label="Per conversation rate" type="number" value={rate} onChange={(e) => setRate(e.target.value)} inputProps={{ min: 0.01, step: 0.01 }} />
        <TextField label="Billing currency" value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} inputProps={{ maxLength: 3 }} />
        </Box>
        <Button sx={{ mt: 2 }} variant="contained" startIcon={<Save />} disabled={saving} onClick={saveCommercialSettings}>
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </> : <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
        <Box><Typography variant="overline" color="text.secondary">Daily maximum cap</Typography><Typography variant="h6">{settings.DAILY_MAX_CAP ?? 149}</Typography></Box>
        <Box><Typography variant="overline" color="text.secondary">Per conversation</Typography><Typography variant="h6">₹{String(settings.PER_CONVERSATION_RATE ?? "2.00")}</Typography></Box>
        <Box><Typography variant="overline" color="text.secondary">Billing currency</Typography><Typography variant="h6">{settings.BILLING_CURRENCY ?? "INR"}</Typography></Box>
      </Box>}
    </Paper>
  </Stack>;
}

const QUICK_ADMIN_COMMANDS = [
  { label: "Help", command: "help" },
  { label: "Disable list", command: "disable list" },
];

function AdminTools({
  settingsId, businessId, editMode,
}: {
  settingsId: string;
  businessId: string;
  editMode: boolean;
}) {
  const [command, setCommand] = useState("help");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [result, setResult] = useState<AdminCommandResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setCommand("help"); setPhoneNumber(""); setResult(null); setError("");
  }, [settingsId]);

  const execute = async (nextCommand = command) => {
    const normalized = nextCommand.trim().toLowerCase();
    if (!normalized) { setError("Enter an admin command."); return; }
    const mutatesSettings = normalized.startsWith("disable add ") || normalized.startsWith("disable remove ");
    if (mutatesSettings && !editMode) {
      setError("Enable global Edit mode before adding or removing a disabled number.");
      return;
    }
    setRunning(true); setError("");
    try {
      const response = await superAdminApi.executeAdminCommand(settingsId, nextCommand.trim());
      setCommand(nextCommand.trim()); setResult(response);
    } catch (e) { setError(messageFor(e)); }
    finally { setRunning(false); }
  };

  const runPhoneCommand = (operation: "add" | "remove") => {
    const digits = phoneNumber.replace(/\D/g, "");
    if (!digits) { setError("Enter a valid phone number first."); return; }
    void execute(`disable ${operation} ${digits}`);
  };

  if (!businessId) {
    return <Alert severity="info">This account has no business ID, so admin commands cannot run.</Alert>;
  }

  return <Stack spacing={2}>
    <Box>
      <Stack direction="row" gap={1} alignItems="center"><Terminal color="primary" /><Typography variant="h6" fontWeight={700}>Admin command tools</Typography></Stack>
      <Typography variant="body2" color="text.secondary">
        Runs the same help and disable-list handlers used by the configured WhatsApp admin flow. Results appear here and are not sent as WhatsApp messages.
      </Typography>
    </Box>
    {error && <Alert severity="error">{error}</Alert>}
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography fontWeight={700} mb={1}>Quick actions</Typography>
      <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
        {QUICK_ADMIN_COMMANDS.map((item) => <Button key={item.command} variant="outlined" disabled={running} onClick={() => void execute(item.command)}>{item.label}</Button>)}
      </Stack>
    </Paper>
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography fontWeight={700}>Manage disabled AI numbers</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>Adding or removing a number requires global Edit mode.</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
        <TextField fullWidth size="small" label="Customer phone number" placeholder="919999999999" value={phoneNumber} disabled={!editMode || running} onChange={(event) => setPhoneNumber(event.target.value)} />
        <Button variant="contained" disabled={!editMode || running} onClick={() => runPhoneCommand("add")}>Disable AI</Button>
        <Button color="error" variant="outlined" disabled={!editMode || running} onClick={() => runPhoneCommand("remove")}>Enable AI</Button>
      </Stack>
    </Paper>
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography fontWeight={700} mb={1}>Run a command</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} gap={1} alignItems="flex-start">
        <TextField fullWidth size="small" label="Admin command" value={command} disabled={running} onChange={(event) => setCommand(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); void execute(); } }} helperText="Supported: help, disable list, disable add <number>, disable remove <number>" />
        <Button variant="contained" startIcon={<PlayArrow />} disabled={running || !command.trim()} onClick={() => void execute()} sx={{ minWidth: 120 }}>{running ? "Running…" : "Run"}</Button>
      </Stack>
    </Paper>
    {result && <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover" }}><Typography fontWeight={700}>{result.command}</Typography><Typography variant="caption" color="text.secondary">{result.action} · {new Date(result.executedAt).toLocaleString()}</Typography></Box>
      <Box component="pre" sx={{ m: 0, p: 2, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "Consolas, Monaco, 'Courier New', monospace", fontSize: 13 }}>{result.output}</Box>
    </Paper>}
  </Stack>;
}

export default function SuperAdminPage() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<SuperAdminBusiness[]>([]);
  const [selected, setSelected] = useState<SuperAdminBusiness | null>(null);
  const [settings, setSettings] = useState<Record<string, any> | null>(null);
  const [agentSettings, setAgentSettings] = useState<Record<string, any> | null>(null);
  const [agentError, setAgentError] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editMode, setEditMode] = useState(false);

  const loadBusinesses = async () => {
    setLoading(true); setError("");
    try { const response = await superAdminApi.businesses(); setBusinesses(response.items); }
    catch (e) { setError(messageFor(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { void loadBusinesses(); }, []);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return businesses.filter((item) => !needle || [item.name, item.businessId, item.phoneNumber, item.settingsId].join(" ").toLowerCase().includes(needle));
  }, [businesses, search]);

  const viewBusiness = async (business: SuperAdminBusiness) => {
    setSelected(business); setSettings(null); setAgentSettings(null); setAgentError("");
    setTab(0); setEditMode(false); setDetailsLoading(true); setError(""); setSuccess("");
    sessionStorage.setItem("superAdminViewedSettingsId", business.settingsId);
    try {
      const [userResult, agentResult] = await Promise.allSettled([
        superAdminApi.businessSettings(business.settingsId),
        superAdminApi.agentSettings(business.settingsId),
      ]);
      if (userResult.status === "rejected") throw userResult.reason;
      setSettings(userResult.value);
      if (agentResult.status === "fulfilled") setAgentSettings(agentResult.value);
      else setAgentError(messageFor(agentResult.reason));
    } catch (e) { setError(messageFor(e)); }
    finally { setDetailsLoading(false); }
  };

  const saveUserJson = async (next: Record<string, any>) => {
    if (!selected) return;
    setError(""); setSuccess("");
    try {
      const saved = await superAdminApi.updateBusinessSettings(selected.settingsId, next);
      setSettings(saved); setSuccess("User settings JSON saved.");
      await loadBusinesses();
    } catch (e) { setError(messageFor(e)); throw e; }
  };

  const saveAgentJson = async (next: Record<string, any>) => {
    if (!selected) return;
    setError(""); setSuccess("");
    try {
      const saved = await superAdminApi.updateAgentSettings(selected.settingsId, next);
      setAgentSettings(saved); setSuccess("Agent settings JSON saved.");
      await loadBusinesses();
    } catch (e) { setError(messageFor(e)); throw e; }
  };

  const applyCommercialUpdate = (update: Record<string, any>) => {
    setSettings((current) => current ? { ...current, ...update } : current);
    setSelected((current) => current ? {
      ...current,
      dailyMaxCap: update.DAILY_MAX_CAP,
      rate: update.PER_CONVERSATION_RATE,
      currency: update.BILLING_CURRENCY,
    } : current);
    setBusinesses((current) => current.map((item) =>
      item.settingsId === selected?.settingsId
        ? {
            ...item,
            dailyMaxCap: update.DAILY_MAX_CAP,
            rate: update.PER_CONVERSATION_RATE,
            currency: update.BILLING_CURRENCY,
          }
        : item,
    ));
  };

  return <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3.5 }, px: { xs: 2, md: 3.5 } }}>
    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} mb={3}>
      <Box sx={{ flex: 1 }}><DashboardHeading title="Super admin" icon={<Shield />} description="Inspect business accounts, review usage and manage agent configuration." /></Box>
      <Stack direction="row" gap={1}><Button startIcon={<Refresh />} onClick={loadBusinesses}>Refresh</Button><Button variant="contained" startIcon={<AssignmentTurnedIn />} onClick={() => navigate("/onboarding")}>Onboarding forms</Button></Stack>
    </Stack>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "280px minmax(0, 1fr)" }, gap: 3 }}>
      <Paper variant="outlined" sx={{ overflow: "hidden", alignSelf: "start" }}>
        <Box p={2}><TextField fullWidth size="small" placeholder="Search name, phone, or ID" value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} /></Box><Divider />
        {loading ? <Box py={6} textAlign="center"><CircularProgress size={28} /></Box> : <List sx={{ maxHeight: "70vh", overflow: "auto" }}>{filtered.map((item) => <ListItemButton key={item.settingsId} selected={selected?.settingsId === item.settingsId} onClick={() => viewBusiness(item)}><ListItemText primary={item.name} secondary={`${item.phoneNumber || "No phone"} · ₹${item.rate} · cap ${item.dailyMaxCap}`} /></ListItemButton>)}{filtered.length === 0 && <Typography p={3} color="text.secondary">No businesses found.</Typography>}</List>}
      </Paper>
      <Paper variant="outlined" sx={{ minHeight: 500, p: { xs: 2, md: 3 } }}>
        {!selected && <Stack alignItems="center" justifyContent="center" minHeight={430} textAlign="center"><Business sx={{ fontSize: 56, color: "text.disabled" }} /><Typography variant="h6">Choose a business account</Typography><Typography color="text.secondary">Customer credentials remain redacted and cannot be replaced through the JSON editor.</Typography></Stack>}
        {selected && <><Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2}><Box><Typography variant="h5" fontWeight={700}>{selected.name}</Typography><Typography color="text.secondary">Viewing account · {selected.settingsId}</Typography></Box><Stack direction="row" gap={1} alignItems="center"><Button startIcon={<AccountTree />} variant="outlined" disabled={!selected.businessId} onClick={() => navigate(`/chatbot-builder/${encodeURIComponent(selected.businessId)}`)}>Chatbot Builder</Button><Chip color="warning" label="Audited super-admin access" /><FormControlLabel control={<Switch checked={editMode} onChange={(_, checked) => { setEditMode(checked); setError(""); setSuccess(""); }} color="warning" />} label={editMode ? "Edit mode on" : "Read only"} sx={{ m: 0, px: 1, border: "1px solid", borderColor: editMode ? "warning.main" : "divider", borderRadius: 2 }} /></Stack></Stack>{editMode && <Alert severity="warning" sx={{ mt: 2 }}>Edit mode applies to Settings, User settings, Agent settings, and mutating Admin tools commands. Changes are saved only when you use the relevant action in that tab.</Alert>}<Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ my: 2 }} variant="scrollable"><Tab label="Settings" /><Tab label="Billing usage" /><Tab label="User settings (redacted)" /><Tab label="Agent settings (redacted)" /><Tab label="Admin tools" /></Tabs>{detailsLoading && <Box py={8} textAlign="center"><CircularProgress /></Box>}{!detailsLoading && tab === 0 && settings && <SettingsOverview settings={settings} editMode={editMode} onSaved={applyCommercialUpdate} />}{!detailsLoading && tab === 1 && (selected.businessId ? <ConversationBillingDashboard businessId={selected.businessId} /> : <Alert severity="info">This account has no WhatsApp business ID, so billing is unavailable.</Alert>)}{!detailsLoading && tab === 2 && settings && <JsonEditor title="User settings raw (redacted)" value={settings} filename={`${selected.settingsId}-user-settings.redacted.json`} editMode={editMode} onSave={saveUserJson} />}{!detailsLoading && tab === 3 && agentSettings && <JsonEditor title="Agent raw (redacted)" value={agentSettings} filename={`${agentSettings.agentId || selected.settingsId}-agent.redacted.json`} editMode={editMode} onSave={saveAgentJson} />}{!detailsLoading && tab === 3 && !agentSettings && <Alert severity="warning">{agentError || "No referenced agent was found for this user settings document."}</Alert>}{!detailsLoading && tab === 4 && <AdminTools settingsId={selected.settingsId} businessId={selected.businessId} editMode={editMode} />}</>}
      </Paper>
    </Box>
  </Container>;
}
